import React, { useEffect, useState } from 'react';
import { ArrowRight, TrendingUp, Shield, Users, Award, ChevronUp, BarChart3, PieChart, Activity } from 'lucide-react';
import { Button } from './ui/button';
import { useSite } from '../SiteContext';

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [stockPrice, setStockPrice] = useState(1589.45);
  const [priceChange, setPriceChange] = useState(2.5);
  const { getSection } = useSite();
  const heroData = getSection('hero');

  useEffect(() => {
    setIsVisible(true);
    
    // Simulate live stock price updates
    const interval = setInterval(() => {
      const change = (Math.random() - 0.45) * 2;
      setStockPrice(prev => Math.max(1500, Math.min(1700, prev + change)));
      setPriceChange(prev => {
        const newChange = prev + (Math.random() - 0.48) * 0.1;
        return Math.max(-5, Math.min(10, newChange));
      });
    }, 2000);
    
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const iconMap = { Users, Award, Shield, TrendingUp };
  const stats = (heroData?.stats || [
    { value: '5,000+', label: 'Pelajar' },
    { value: '10+', label: 'Tahun Pengalaman' },
    { value: '100%', label: 'Patuh Syariah' },
  ]).map((s, i) => ({ ...s, icon: [Users, Award, Shield][i] || Shield }));

  return (
    <section id="hero" className="relative overflow-hidden min-h-screen" style={{ backgroundColor: '#023a54' }}>
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Floating geometric shapes */}
        <div className="absolute top-20 left-10 w-64 h-64 rounded-full opacity-10 animate-blob" 
             style={{ background: 'linear-gradient(135deg, #1ec7a0, #507789)', filter: 'blur(40px)' }}></div>
        <div className="absolute top-40 right-20 w-96 h-96 rounded-full opacity-10 animate-blob animation-delay-2000" 
             style={{ background: 'linear-gradient(135deg, #507789, #1ec7a0)', filter: 'blur(60px)' }}></div>
        <div className="absolute bottom-20 left-1/3 w-80 h-80 rounded-full opacity-10 animate-blob animation-delay-4000" 
             style={{ background: 'linear-gradient(135deg, #1ec7a0, #023a54)', filter: 'blur(50px)' }}></div>
        
        {/* Grid pattern overlay */}
        <div className="absolute inset-0 opacity-5" 
             style={{ 
               backgroundImage: 'linear-gradient(#1ec7a0 1px, transparent 1px), linear-gradient(90deg, #1ec7a0 1px, transparent 1px)',
               backgroundSize: '50px 50px'
             }}></div>
             
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 rounded-full animate-float-particle"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              backgroundColor: '#1ec7a0',
              opacity: 0.3,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 5}s`
            }}
          ></div>
        ))}
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className={`text-white transform transition-all duration-1000 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-full glass-morphism animate-pulse-subtle" 
                 style={{ backgroundColor: 'rgba(30, 199, 160, 0.15)', border: '1px solid rgba(30, 199, 160, 0.4)' }}>
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: '#1ec7a0' }}></span>
                <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: '#1ec7a0' }}></span>
              </span>
              <span className="text-sm font-semibold" style={{ color: '#1ec7a0' }}>{heroData?.subtitle || 'Platform Pendidikan Saham Patuh Syariah di Malaysia'}</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="inline-block animate-text-reveal">Mula Belajar Saham</span>
              <span className="block mt-2">
                <span className="text-gradient-animated">Dengan Betul</span>
              </span>
            </h1>
            
            <p className="text-lg md:text-xl mb-8 opacity-90 leading-relaxed animate-fade-in-up animation-delay-300">
              Dapatkan ilmu pelaburan saham yang <strong className="text-[#1ec7a0]">praktikal, mudah faham, dan 100% patuh syariah</strong>. 
              Dari beginner hingga mahir, kami guide anda step by step.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12 animate-fade-in-up animation-delay-500">
              <Button
                onClick={() => scrollToSection('classes')}
                className="btn-lg group font-semibold rounded-xl relative overflow-hidden transform hover:scale-105 transition-all duration-300"
                style={{ backgroundColor: '#1ec7a0' }}
                data-testid="hero-cta-classes"
              >
                <span className="relative z-10 flex items-center">
                  Lihat Kelas Kami
                  <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform duration-300" size={20} />
                </span>
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </Button>
              <Button
                onClick={() => scrollToSection('calculator')}
                variant="outline"
                className="btn-lg font-semibold rounded-xl border-2 border-white/50 text-white hover:bg-white hover:text-[#023a54] transform hover:scale-105 transition-all duration-300 backdrop-blur-sm"
                data-testid="hero-cta-calculator"
              >
                Cuba Kalkulator Percuma
              </Button>
            </div>

            {/* Stats with 3D hover effect */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 lg:gap-6">
              {stats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <div 
                    key={index} 
                    className="stat-card-3d p-4 rounded-xl cursor-pointer transform transition-all duration-500 hover:scale-110"
                    style={{ 
                      animationDelay: `${index * 0.15}s`,
                      background: 'rgba(255,255,255,0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255,255,255,0.1)'
                    }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(30, 199, 160, 0.2)' }}>
                        <Icon size={18} style={{ color: '#1ec7a0' }} />
                      </div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                    </div>
                    <div className="text-sm opacity-80">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Content - Trader Scene with Enhanced Animations */}
          <div className={`hidden lg:block transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
            <div className="relative perspective-1000">
              {/* Main Trader Image with 3D effect */}
              <div className="relative z-10 transform-3d hover:rotate-y-5 transition-transform duration-700">
                <div className="relative rounded-3xl overflow-hidden shadow-2xl"
                     style={{ 
                       border: '3px solid rgba(30, 199, 160, 0.4)',
                       boxShadow: '0 25px 80px rgba(30, 199, 160, 0.3), 0 10px 40px rgba(0,0,0,0.3)'
                     }}>
                  <img
                    src="https://static.prod-images.emergentagent.com/jobs/ddb4f9b5-94d9-46ac-a649-e0d7b8f2100c/images/0ad7e55f918129d4a68b840767f3177687bc3b0e2fbecde46b24e2da75ab36aa.png"
                    alt="Trader profesional sedang trade di depan monitor"
                    className="w-full h-auto object-cover transform hover:scale-105 transition-transform duration-700"
                    data-testid="hero-trader-image"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#023a54]/60 via-transparent to-transparent"></div>
                </div>
                
                {/* Live Stock Ticker Card - Animated */}
                <div className="absolute -top-4 -right-4 bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-2xl animate-float z-20" 
                     style={{ width: '260px', border: '1px solid rgba(30, 199, 160, 0.2)' }}>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">KLSE INDEX</p>
                      <p className="text-2xl font-bold transition-all duration-300" style={{ color: priceChange >= 0 ? '#1ec7a0' : '#ef4444' }}>
                        {stockPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-sm font-bold ${priceChange >= 0 ? 'bg-green-100' : 'bg-red-100'}`}
                           style={{ color: priceChange >= 0 ? '#1ec7a0' : '#ef4444' }}>
                        <ChevronUp className={`w-4 h-4 transition-transform ${priceChange < 0 ? 'rotate-180' : ''}`} />
                        {Math.abs(priceChange).toFixed(2)}%
                      </div>
                      <p className="text-xs text-gray-400 mt-1">Live</p>
                    </div>
                  </div>
                  
                  {/* Enhanced Animated Chart */}
                  <div className="relative h-20 overflow-hidden rounded-lg" style={{ backgroundColor: 'rgba(30, 199, 160, 0.05)' }}>
                    <svg viewBox="0 0 250 80" className="w-full h-full" preserveAspectRatio="none">
                      <defs>
                        <linearGradient id="chartGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" style={{ stopColor: '#1ec7a0', stopOpacity: 0.4 }} />
                          <stop offset="100%" style={{ stopColor: '#1ec7a0', stopOpacity: 0.05 }} />
                        </linearGradient>
                      </defs>
                      
                      {/* Animated filled area */}
                      <path
                        d="M 0 65 Q 20 60, 40 55 T 80 45 T 120 40 T 160 35 T 200 25 L 250 20 L 250 80 L 0 80 Z"
                        fill="url(#chartGradient2)"
                        className="animate-chart-fill"
                      />
                      
                      {/* Animated line */}
                      <path
                        d="M 0 65 Q 20 60, 40 55 T 80 45 T 120 40 T 160 35 T 200 25 L 250 20"
                        stroke="#1ec7a0"
                        strokeWidth="2.5"
                        fill="none"
                        strokeLinecap="round"
                        className="animate-draw-chart"
                      />
                      
                      {/* Glowing dot at end */}
                      <circle cx="250" cy="20" r="6" fill="#1ec7a0" className="animate-pulse">
                        <animate attributeName="r" values="4;7;4" dur="2s" repeatCount="indefinite" />
                      </circle>
                      <circle cx="250" cy="20" r="10" fill="#1ec7a0" opacity="0.3" className="animate-ping" />
                    </svg>
                  </div>
                </div>
                
                {/* Portfolio Growth Card - Bottom Left */}
                <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl p-4 shadow-2xl animate-float z-20" 
                     style={{ animationDelay: '1s', border: '1px solid rgba(30, 199, 160, 0.2)' }}>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center relative"
                         style={{ background: 'linear-gradient(135deg, rgba(30, 199, 160, 0.2), rgba(30, 199, 160, 0.1))' }}>
                      <TrendingUp size={28} style={{ color: '#1ec7a0' }} />
                      <div className="absolute inset-0 rounded-xl animate-ping opacity-20" style={{ backgroundColor: '#1ec7a0' }}></div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Portfolio Growth</p>
                      <p className="text-2xl font-bold" style={{ color: '#1ec7a0' }}>+24.8%</p>
                    </div>
                  </div>
                </div>
                
                {/* Mini Stats Cards - Floating */}
                <div className="absolute top-1/2 -left-8 bg-white/90 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float-slow z-20"
                     style={{ animationDelay: '0.5s' }}>
                  <div className="flex items-center gap-2">
                    <BarChart3 size={20} style={{ color: '#1ec7a0' }} />
                    <span className="text-sm font-bold text-gray-700">+12 Trades</span>
                  </div>
                </div>
                
                <div className="absolute top-1/4 -right-6 bg-white/90 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float-slow z-20"
                     style={{ animationDelay: '1.5s' }}>
                  <div className="flex items-center gap-2">
                    <PieChart size={20} style={{ color: '#507789' }} />
                    <span className="text-sm font-bold text-gray-700">Diversified</span>
                  </div>
                </div>
                
                <div className="absolute bottom-1/3 -right-10 bg-[#023a54] rounded-xl p-3 shadow-xl animate-float-slow z-20"
                     style={{ animationDelay: '2s' }}>
                  <div className="flex items-center gap-2">
                    <Activity size={20} style={{ color: '#1ec7a0' }} />
                    <span className="text-sm font-bold text-white">Active</span>
                  </div>
                </div>
              </div>
              
              {/* Enhanced Background Glow Effects */}
              <div className="absolute -inset-4 rounded-3xl animate-glow-pulse" 
                   style={{ 
                     background: 'radial-gradient(ellipse at center, rgba(30, 199, 160, 0.3) 0%, transparent 70%)', 
                     filter: 'blur(40px)', 
                     zIndex: 0 
                   }}></div>
              <div className="absolute -inset-8 rounded-3xl animate-glow-pulse animation-delay-2000" 
                   style={{ 
                     background: 'radial-gradient(ellipse at center, rgba(80, 119, 137, 0.2) 0%, transparent 70%)', 
                     filter: 'blur(60px)', 
                     zIndex: 0 
                   }}></div>
            </div>
          </div>
        </div>
      </div>

      {/* Animated Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path 
            d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
            fill="white"
            className="animate-wave"
          />
        </svg>
      </div>
    </section>
  );
};

export default Hero;